// Builds a dedicated, print-optimised recipe sheet (not a copy of the on-screen modal)
// and prints it from a hidden iframe so no pop-up window is needed.

const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const PRINT_CSS = `
  @page { size: A4; margin: 16mm 15mm 18mm; }
  * { box-sizing: border-box; }
  html { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body {
    margin: 0;
    font-family: 'Figtree', 'Segoe UI', Helvetica, Arial, sans-serif;
    font-size: 11pt;
    line-height: 1.55;
    color: #2a1f17;
    background: #fff;
  }
  .sheet { max-width: 180mm; margin: 0 auto; }

  .masthead {
    display: flex; justify-content: space-between; align-items: baseline;
    padding-bottom: 8pt; margin-bottom: 18pt;
    border-bottom: 2pt solid #2a1f17;
  }
  .brand { font-family: 'Fraunces', Georgia, 'Times New Roman', serif; font-size: 17pt; font-weight: 700; letter-spacing: -0.01em; }
  .brand span { color: #e0522f; }
  .masthead small { font-size: 8.5pt; letter-spacing: 0.14em; text-transform: uppercase; color: #6b5d4f; font-weight: 600; }

  h1 {
    font-family: 'Fraunces', Georgia, 'Times New Roman', serif;
    font-size: 27pt; line-height: 1.12; font-weight: 600;
    letter-spacing: -0.02em; margin: 0 0 12pt;
  }

  .hero { display: flex; gap: 16pt; align-items: stretch; margin-bottom: 20pt; }
  .hero img {
    width: 78mm; height: 58mm; object-fit: cover; flex-shrink: 0;
    border-radius: 8pt; border: 0.75pt solid #d9cfbf;
  }
  .hero-side { display: flex; flex-direction: column; justify-content: center; gap: 10pt; flex: 1; }

  .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 8pt; }
  .meta div {
    padding: 7pt 10pt; background: #f6efe0; border-radius: 6pt;
    border-left: 3pt solid #0f766e;
  }
  .meta dt { font-size: 7.5pt; letter-spacing: 0.12em; text-transform: uppercase; color: #6b5d4f; font-weight: 700; margin: 0; }
  .meta dd { margin: 1pt 0 0; font-weight: 600; font-size: 11pt; }

  .tags { display: flex; flex-wrap: wrap; gap: 5pt; }
  .tags span { font-size: 8.5pt; font-weight: 600; padding: 2pt 9pt; border-radius: 99pt; background: #fde3d9; color: #c43f1e; }

  .layout { display: grid; grid-template-columns: 62mm 1fr; gap: 20pt; align-items: start; }

  h2 {
    font-family: 'Fraunces', Georgia, 'Times New Roman', serif;
    font-size: 15pt; font-weight: 600; margin: 0 0 9pt; padding-bottom: 5pt;
    border-bottom: 0.75pt solid #d9cfbf; position: relative;
    break-after: avoid;
  }
  h2::after { content: ''; position: absolute; left: 0; bottom: -0.75pt; width: 26pt; height: 2.25pt; background: #e0522f; }

  .ingredients { padding: 12pt 13pt 6pt; background: #faf5ea; border: 0.75pt solid #e6dcc9; border-radius: 8pt; }
  .ingredients ul { list-style: none; margin: 0; padding: 0; }
  .ingredients li {
    display: flex; gap: 8pt; align-items: flex-start;
    padding: 4.5pt 0; border-bottom: 0.5pt dashed #d9cfbf;
    font-size: 10pt; line-height: 1.4; break-inside: avoid;
  }
  .ingredients li:last-child { border-bottom: none; }
  .ingredients li::before {
    content: ''; flex-shrink: 0; width: 8pt; height: 8pt; margin-top: 2.5pt;
    border: 1pt solid #0f766e; border-radius: 2pt; background: #fff;
  }

  ol.steps { list-style: none; counter-reset: step; margin: 0; padding: 0; }
  ol.steps li {
    counter-increment: step; display: flex; gap: 11pt; align-items: flex-start;
    margin-bottom: 11pt; break-inside: avoid;
  }
  ol.steps li::before {
    content: counter(step); flex-shrink: 0; width: 20pt; height: 20pt;
    display: flex; align-items: center; justify-content: center;
    border-radius: 50%; background: #0f766e; color: #fff;
    font-family: 'Fraunces', Georgia, serif; font-weight: 700; font-size: 10pt;
  }
  ol.steps li p { margin: 1pt 0 0; font-size: 10.5pt; }
  ol.steps li .time { display: inline-block; margin-left: 4pt; font-size: 8pt; font-weight: 700; color: #0f766e; background: #d6efe9; padding: 1pt 7pt; border-radius: 99pt; }

  .foot {
    margin-top: 22pt; padding-top: 8pt; border-top: 0.75pt solid #d9cfbf;
    display: flex; justify-content: space-between;
    font-size: 8.5pt; color: #9a8c7c;
  }

  @media screen { body { padding: 20px; } }
  @media print { .layout { gap: 18pt; } }
`;

function buildRecipeHtml({ title, image, meta = [], tags = [], ingredients = [], steps = [] }) {
  const metaHtml = meta
    .filter((m) => m.value)
    .map((m) => `<div><dt>${esc(m.label)}</dt><dd>${esc(m.value)}</dd></div>`)
    .join("");
  const tagsHtml = tags.map((t) => `<span>${esc(t)}</span>`).join("");

  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${esc(title)} — Recify</title>
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Figtree:wght@400;500;600;700&display=swap" />
<style>${PRINT_CSS}</style>
</head>
<body>
  <div class="sheet">
    <div class="masthead">
      <div class="brand">Recify<span>.</span></div>
      <small>Recipe card</small>
    </div>

    <h1>${esc(title)}</h1>

    <div class="hero">
      ${image ? `<img src="${esc(image)}" alt="" />` : ""}
      <div class="hero-side">
        ${metaHtml ? `<dl class="meta" style="margin:0">${metaHtml}</dl>` : ""}
        ${tagsHtml ? `<div class="tags">${tagsHtml}</div>` : ""}
      </div>
    </div>

    <div class="layout">
      <section class="ingredients">
        <h2>Ingredients</h2>
        <ul>${ingredients.map((i) => `<li><span>${esc(i)}</span></li>`).join("")}</ul>
      </section>
      <section>
        <h2>Instructions</h2>
        <ol class="steps">${steps
          .map(
            (s) =>
              `<li><p>${esc(s.text)}${s.minutes ? `<span class="time">${esc(s.minutes)} min</span>` : ""}</p></li>`
          )
          .join("")}</ol>
      </section>
    </div>

    <div class="foot">
      <span>Printed from Recify</span>
      <span>${new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</span>
    </div>
  </div>
</body>
</html>`;

  return html;
}

/** Prints an HTML document string from a hidden iframe (no pop-up window). */
export function printDocument(html) {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText =
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
  document.body.appendChild(iframe);

  const cleanup = () => setTimeout(() => iframe.remove(), 500);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();

  // Wait for fonts + hero image so the print preview is complete
  const win = iframe.contentWindow;
  const ready = Promise.all([
    doc.fonts?.ready?.catch(() => {}),
    ...Array.from(doc.images).map(
      (img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((res) => {
              img.onload = img.onerror = res;
            })
    ),
  ]);
  const timeout = new Promise((res) => setTimeout(res, 4000));

  Promise.race([ready, timeout]).then(() => {
    win.focus();
    win.onafterprint = cleanup;
    win.print();
    // Fallback if afterprint never fires
    setTimeout(() => iframe.remove(), 60000);
  });
}

export function printRecipe(data) {
  printDocument(buildRecipeHtml(data));
}

const SHOPPING_CSS = `
  .group { margin-bottom: 16pt; break-inside: avoid; }
  .group h2 { font-size: 13pt; }
  .group ul { list-style: none; margin: 0; padding: 0; columns: 2; column-gap: 22pt; }
  .group li { display: flex; gap: 8pt; padding: 4pt 0; border-bottom: 0.5pt dashed #d9cfbf; break-inside: avoid; font-size: 10.5pt; }
  .group li::before { content: ''; flex-shrink: 0; width: 8pt; height: 8pt; margin-top: 3pt; border: 1pt solid #0f766e; border-radius: 2pt; }
  .group li.done { color: #9a8c7c; text-decoration: line-through; }
  .group li.done::before { background: #0f766e; }
`;

/** groups: [{ title, items: [{ text, checked }] }] */
export function printShoppingList(groups) {
  const body = groups
    .map(
      (g) => `<section class="group"><h2>${esc(g.title)}</h2><ul>${g.items
        .map((i) => `<li class="${i.checked ? "done" : ""}"><span>${esc(i.text)}</span></li>`)
        .join("")}</ul></section>`
    )
    .join("");
  const html = `<!doctype html><html lang="en"><head><meta charset="utf-8" />
<title>Shopping list — Recify</title>
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Figtree:wght@400;500;600;700&display=swap" />
<style>${PRINT_CSS}${SHOPPING_CSS}</style></head><body><div class="sheet">
<div class="masthead"><div class="brand">Recify<span>.</span></div><small>Shopping list</small></div>
<h1>Shopping list</h1>${body}
<div class="foot"><span>Printed from Recify</span><span>${new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</span></div>
</div></body></html>`;
  printDocument(html);
}

export default printRecipe;
